import getConfig from 'next/config'

import { useQueryClientDefaults } from '@fe-toolkit/react-query-hooks-toolkit'
import { QueryClientProvider as ToolkitQueryClientProvider } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { redirectToLogin, ECommonApiUrls } from '@giffgaff/broadband-common'

import { useError } from '@/context'

const { publicRuntimeConfig } = getConfig()

const { HOST_URL, AUTHENTICATION_PATH_URL } = publicRuntimeConfig

const QueryClientProvider = ({ children }: { children: React.ReactNode }) => {
  const { setErrorCode, setErrorMessage, setIsRedirectingToLogin } = useError()

  // Redirect to the login page on a different MFE using window.location
  const redirectToLoginWithQuery = (loginRedirectUrl?: string) => {
    redirectToLogin({
      baseUrl: HOST_URL,
      authPathUrl: AUTHENTICATION_PATH_URL,
      redirectPath: loginRedirectUrl,
      referrer: 'broadband',
    })
  }

  const queryClient = useQueryClientDefaults({
    onMutationUnauthenticated(error, _variables, context) {
      const _context = context as { loginRedirectUrl: string }

      setIsRedirectingToLogin(true)
      redirectToLoginWithQuery(_context?.loginRedirectUrl)
    },
    onMutationError(error, _variables, _context) {
      if (isAxiosError(error) && !!error.response && error?.response.status === 500) {
        setErrorMessage(error.response.data.message)
        setErrorCode(500)
      }
    },
    onQueryError(error, _query) {
      if (isAxiosError(error) && error?.response?.status === 500) {
        setErrorMessage(error?.response?.data?.message)
        setErrorCode(500)
      }
    },
    onQueryUnauthenticated(error, _variables, context) {
      const _context = context as { loginRedirectUrl: string }

      const unauthenticatedWhiteList: string[] = [ECommonApiUrls.INTERCOM_ME]

      if (isAxiosError(error)) {
        const url = error?.config?.url ?? ''

        // if the request is part of the unauthenticated white list, do not redirect to login
        if (unauthenticatedWhiteList.includes(url)) {
          return
        }
      }

      setIsRedirectingToLogin(true)
      redirectToLoginWithQuery(_context?.loginRedirectUrl)

      queryClient.setDefaultOptions({
        queries: {
          staleTime: 60 * 5000, // 5 minutes
          retry: false,
          refetchOnWindowFocus: false,
          refetchOnMount: false,
          refetchOnReconnect: false,
          refetchInterval: false,
          refetchIntervalInBackground: false,
        },
      })
    },
  })

  return <ToolkitQueryClientProvider client={queryClient}>{children}</ToolkitQueryClientProvider>
}

export default QueryClientProvider
